"use client"

import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import Image from "next/image"
import { useRouter, usePathname } from "next/navigation"
import { Link } from "utils"
import { useCallback, useEffect, useMemo, useState } from "react"
import SearchParamsListener from "../../common/SearchParamsListener"
import "swiper/css"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import type { components } from "umbraco"
import Container from "../../Container"
import { getPaginationWindow } from "../../common/paginationWindow"
import ArrowLeft from "../../icons/ArrowLeft"
import ChevronBullet from "../../icons/ChevronBullet"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import { breakpoints } from "../../theme/mediaQueries"
import {
  ArticleCard,
  BadgeText,
  CardContent,
  CardExcerpt,
  CardFooter,
  CardGrid,
  CardHeading,
  CardImageWrapper,
  CardTitle,
  CategoryBadge,
  ChevronWrapper,
  CtaArrow,
  CtaLink,
  CtaText,
  DateText,
  DesktopImage,
  InnerHero,
  MobileImage,
  PageButton,
  PageButtonText,
  PageSubtitle,
  PageTitle,
  PaginationArrow,
  PaginationContainer,
  Root,
  SwiperWrapper,
  TextContainer,
  TitleRow,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type MagazineLobbyData = components["schemas"]["MagazineLobbyPropertiesModel"]

interface ArticleItem {
  title: string
  excerpt: string
  category: string
  /** EGDE-398: all categories the article carries (primary + secondary). */
  categories?: string[]
  publishDate: string
  imageUrl: string | null
  mobileImageUrl?: string | null
  slug: string
  routePath: string
}

interface Props {
  data: MagazineLobbyData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  articleListings?: ArticleItem[]
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALL_KEY = "__all__"
const DEFAULT_ITEMS_PER_PAGE = 6

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    const day = String(d.getDate()).padStart(2, "0")
    const month = String(d.getMonth() + 1).padStart(2, "0")
    const year = String(d.getFullYear()).slice(-2)
    return `${day}/${month}/${year}`
  } catch {
    return ""
  }
}

// EGDE-375: category values come from a MultiNodeTreePicker of managed category
// nodes, so the Delivery API serialises them as objects ({ name, … }) rather
// than plain strings. Resolve to label strings; the string branch keeps any
// not-yet-migrated value rendering instead of breaking the tab row.
function getCategoryNames(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => {
      if (typeof entry === "string") return entry
      if (entry && typeof entry === "object") {
        return ((entry as Record<string, unknown>).name as string) ?? ""
      }
      return ""
    })
    .filter((name): name is string => Boolean(name))
}

// Desktop = grid + page pagination; mobile = swipeable card carousel (EGDE-272).
// Gated behind `mounted` so the first paint (and SSR) always renders the grid —
// Swiper never runs on the server, avoiding hydration mismatch.
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoints.lg}px)`)
    setIsDesktop(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])
  return isDesktop
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function MagazineLobby({
  data,
  culture,
  articleListings,
}: Props) {
  const isRtl = culture === "he-il" || culture === "he-IL"
  // Search params arrive from <SearchParamsListener> on mount rather than from
  // useSearchParams() directly. Calling that hook here would bail this whole
  // block out of static rendering, so the lobby could not be prerendered and
  // /magazine returned a 500 once the route became statically cached. Empty on
  // the server, which renders page 1 — the same page an unparameterised URL
  // shows anyway, since pagination slices an in-memory list client-side.
  const [searchParams, setSearchParams] = useState<URLSearchParams>(
    () => new URLSearchParams(),
  )
  const router = useRouter()
  const pathname = usePathname()

  // Category tabs from CMS
  const allLabel = isRtl ? "הכל" : "All"
  // EGDE-375: the lobby "Category Options" picker is an optional ordering hint —
  // resolve the picked category nodes to their label strings. The real tab set
  // is derived from the categories the articles actually use (see categoryTabs).
  const categoryOptions = useMemo(
    () => getCategoryNames(data.categoryOptions),
    [data.categoryOptions],
  )

  // CTA label
  const ctaLabel = isRtl ? "לקרוא עוד" : "Read more"

  // Items per page
  const itemsPerPage = data.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE

  // Responsive mode. EGDE-385/396: the mobile lobby is a PAGINATED GRID by
  // default (respecting "Items Per Page", so 6-set shows 6 per page — not one
  // card per swipe). Editors opt into the swipe carousel with the "Enable
  // Carousel" toggle (returned per QA; matches the regular lobby and Figma).
  const enableCarousel =
    (data as { enableCarousel?: boolean }).enableCarousel === true

  // EGDE-435: the "Enable Category Filter" toggle (Filters tab) controls the
  // whole tab row — it disappeared from the CMS during the EGDE-375 taxonomy
  // rework and QA flagged it as missing. Restored on the magazineLobby element
  // type; the tabs render only when the editor switches it on.
  const enableCategoryFilter =
    (data as { enableCategoryFilter?: boolean }).enableCategoryFilter === true
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const isDesktop = useIsDesktop()
  const showCarousel = mounted && !isDesktop && enableCarousel

  // State
  const [activeCategory, setActiveCategory] = useState(ALL_KEY)
  const [currentPage, setCurrentPage] = useState(1)

  // Mobile carousel state
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)

  // Sync page state with URL search params on mount and URL changes
  useEffect(() => {
    const pageParam = searchParams.get("page")
    const parsed = pageParam ? parseInt(pageParam, 10) : 1
    const page = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1
    setCurrentPage(page)
  }, [searchParams])

  // Use injected article listings
  const allArticles = useMemo(() => articleListings ?? [], [articleListings])

  // The categories an article carries — EGDE-398 exposes the full list; fall
  // back to the single badge category for any not-yet-migrated listing.
  const catsOf = useCallback(
    (a: ArticleItem): string[] =>
      a.categories && a.categories.length > 0
        ? a.categories
        : a.category
          ? [a.category]
          : [],
    [],
  )

  // Category tabs. EGDE-397: the "Category Options" picker is the SOURCE OF
  // TRUTH for the tab row — show exactly the categories the editor selected, in
  // their order, and nothing else. (The previous EGDE-375 behaviour derived the
  // row from whatever categories the articles happened to carry, which surfaced
  // stray categories like "תג 1"/"תג 2" that were never picked.) When the picker
  // is empty we fall back to every category the articles use so the lobby still
  // has tabs.
  const derivedCategories = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const article of allArticles) {
      for (const cat of catsOf(article)) {
        if (cat && !seen.has(cat)) {
          seen.add(cat)
          result.push(cat)
        }
      }
    }
    return result
  }, [allArticles, catsOf])
  const categoryTabs = useMemo(
    () => (categoryOptions.length > 0 ? categoryOptions : derivedCategories),
    [categoryOptions, derivedCategories],
  )

  // Filter articles by category. EGDE-398: match ANY of the article's
  // categories (primary or secondary), not just the first/badge one.
  const filteredArticles = useMemo(() => {
    if (activeCategory === ALL_KEY) return allArticles
    return allArticles.filter((article) =>
      catsOf(article).includes(activeCategory),
    )
  }, [allArticles, activeCategory, catsOf])

  // Pagination (desktop grid)
  const totalPages = Math.max(
    1,
    Math.ceil(filteredArticles.length / itemsPerPage),
  )
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  )

  // URL sync helper
  const updatePageUrl = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString())
      if (page <= 1) {
        params.delete("page")
      } else {
        params.set("page", String(page))
      }
      const query = params.toString()
      router.replace(`${pathname}${query ? `?${query}` : ""}`, {
        scroll: false,
      })
    },
    [searchParams, router, pathname],
  )

  // Handlers
  const handleCategoryChange = useCallback(
    (_: unknown, value: string) => {
      setActiveCategory(value)
      setCurrentPage(1)
      setActiveSlide(0)
      swiper?.slideTo(0)
      const params = new URLSearchParams(searchParams.toString())
      params.delete("page")
      const query = params.toString()
      router.replace(`${pathname}${query ? `?${query}` : ""}`, {
        scroll: false,
      })
    },
    [searchParams, router, pathname, swiper],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      updatePageUrl(page)
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    [updatePageUrl],
  )

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.max(1, prev - 1)
      updatePageUrl(next)
      return next
    })
  }, [updatePageUrl])

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.min(totalPages, prev + 1)
      updatePageUrl(next)
      return next
    })
  }, [totalPages, updatePageUrl])

  // Article card — shared by the desktop grid and the mobile carousel slides.
  const renderCard = (article: ArticleItem) => (
    <ArticleCard key={article.slug} as={Link} href={article.routePath}>
      {/* Card image with category badge */}
      {article.imageUrl && (
        <CardImageWrapper>
          {article.mobileImageUrl ? (
            <>
              {/* EGDE-275: honour the CMS mobile-image override. */}
              <DesktopImage>
                <Image
                  src={article.imageUrl}
                  alt={article.title}
                  fill
                  sizes="(max-width: 1279px) 100vw, 384px"
                  style={{ objectFit: "cover" }}
                />
              </DesktopImage>
              <MobileImage>
                <Image
                  src={article.mobileImageUrl}
                  alt={article.title}
                  fill
                  sizes="(max-width: 1279px) 100vw, 384px"
                  style={{ objectFit: "cover" }}
                />
              </MobileImage>
            </>
          ) : (
            <Image
              src={article.imageUrl}
              alt={article.title}
              fill
              sizes="(max-width: 1279px) 100vw, 384px"
              style={{ objectFit: "cover" }}
            />
          )}
          {article.category && (
            <CategoryBadge>
              <BadgeText variant="body1">{article.category}</BadgeText>
            </CategoryBadge>
          )}
        </CardImageWrapper>
      )}

      <CardContent>
        <CardHeading>
          <CardTitle variant="body1">{article.title}</CardTitle>
          {article.excerpt && (
            <CardExcerpt variant="body2">{article.excerpt}</CardExcerpt>
          )}
        </CardHeading>

        {/* Card footer: source [DateText, CtaLink]
            RTL visual: [Date RIGHT, CTA LEFT].
            CtaLink has margin-inline-start:auto so the read-more
            stays at the inline-end even when no date is present. */}
        <CardFooter>
          {article.publishDate && (
            <DateText variant="body2">
              {formatDate(article.publishDate)}
            </DateText>
          )}
          <CtaLink>
            <CtaText variant="body1">{ctaLabel}</CtaText>
            <CtaArrow>
              <ArrowLeft width={11} height={8} />
            </CtaArrow>
          </CtaLink>
        </CardFooter>
      </CardContent>
    </ArticleCard>
  )

  return (
    <Container paddingMobile>
      <SearchParamsListener onChange={setSearchParams} />
      <Root>
        {/* Hero section: title, subtitle, category tabs */}
        <InnerHero>
          <TextContainer>
            <TitleRow>
              <ChevronWrapper>
                <ChevronBullet width={19} height={32} />
              </ChevronWrapper>
              <PageTitle variant="h1" component="h2">{data.pageTitle}</PageTitle>
            </TitleRow>
            {data.pageSubtitle && (
              <PageSubtitle variant="body1">{data.pageSubtitle}</PageSubtitle>
            )}
          </TextContainer>

          {/* Category tabs — scrollable. EGDE-274: when the categories overflow
              the row must scroll with a visible chevron scroller, matching the
              career lobby (QA: "the scroller should be like career"). Touch swipe
              still works; the chevron just makes the affordance visible. */}
          {enableCategoryFilter && categoryTabs.length > 0 && (
            <MuiTabs
              value={activeCategory}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <MuiTab label={allLabel} value={ALL_KEY} />
              {categoryTabs.map((cat) => (
                <MuiTab key={cat} label={cat} value={cat} />
              ))}
            </MuiTabs>
          )}
        </InnerHero>

        {/* Article cards — mobile carousel (EGDE-272) / desktop grid */}
        {showCarousel ? (
          <SwiperWrapper>
            <Swiper
              dir={isRtl ? "rtl" : "ltr"}
              slidesPerView={1.1}
              spaceBetween={24}
              centeredSlides
              centeredSlidesBounds
              onSwiper={setSwiper}
              onSlideChange={(s) => setActiveSlide(s.activeIndex)}
            >
              {filteredArticles.map((article) => (
                <SwiperSlide key={article.slug}>
                  {renderCard(article)}
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperWrapper>
        ) : (
          <CardGrid>{paginatedArticles.map(renderCard)}</CardGrid>
        )}

        {/* Desktop pagination — numbered pages
            Source: [ChevronRight, pages, ChevronLeft]
            RTL visual: [< LEFT, pages, > RIGHT] */}
        {!showCarousel && totalPages > 1 && (
          <PaginationContainer>
            <PaginationArrow
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronRight width={7} height={16} />
            </PaginationArrow>

            {/* EGDE-433: at most 5 page circles; the window slides with the
                current page. */}
            {getPaginationWindow(currentPage, totalPages).map((page) => (
              <PageButton
                key={page}
                $active={page === currentPage}
                onClick={() => handlePageChange(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? "page" : undefined}
              >
                <PageButtonText $active={page === currentPage}>
                  {page}
                </PageButtonText>
              </PageButton>
            ))}

            <PaginationArrow
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronLeft width={7} height={16} />
            </PaginationArrow>
          </PaginationContainer>
        )}

        {/* Mobile carousel pagination — numbered, one per slide (EGDE-272) */}
        {showCarousel && filteredArticles.length > 1 && (
          <PaginationContainer>
            <PaginationArrow
              onClick={() => swiper?.slidePrev()}
              disabled={activeSlide <= 0}
              aria-label="Previous slide"
            >
              <ChevronRight width={7} height={16} />
            </PaginationArrow>

            {/* EGDE-433: cap the dots at a sliding window of 5. */}
            {getPaginationWindow(activeSlide + 1, filteredArticles.length).map(
              (page) => (
                <PageButton
                  key={page}
                  $active={page - 1 === activeSlide}
                  onClick={() => swiper?.slideTo(page - 1)}
                  aria-label={`Slide ${page}`}
                  aria-current={page - 1 === activeSlide ? "true" : undefined}
                >
                  <PageButtonText $active={page - 1 === activeSlide}>
                    {page}
                  </PageButtonText>
                </PageButton>
              ),
            )}

            <PaginationArrow
              onClick={() => swiper?.slideNext()}
              disabled={activeSlide >= filteredArticles.length - 1}
              aria-label="Next slide"
            >
              <ChevronLeft width={7} height={16} />
            </PaginationArrow>
          </PaginationContainer>
        )}
      </Root>
    </Container>
  )
}
